import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import siteSetup from 'src/js/modules/site-setup';
import { designSystem } from '@yola/ws-ui';
import HeaderGalleryPageContainer from 'src/js/containers/header-gallery-page-container';
import GalleryPagination from 'src/js/common/components/gallery-pagination';
import GalleryGridSection from 'src/js/common/components/gallery-grid-section';
import { PREVIEW_COLUMNS_COUNT } from 'src/js/constants/common';
import SiteSetupLayout from '../site-setup-layout';
import Layout from '../../layout';
import GalleryGrid from '../../gallery-grid';
import CategoriesBar from '../../categories-bar';

const { helpers } = siteSetup;

const { FloatingAction } = designSystem;

const defaultRecommendedTemplates = [];

function GalleryLayoutStep({
  captions,
  control,
  templates,
  canLoadMore,
  recommendedTemplates = defaultRecommendedTemplates,
  categoriesBarRef,
  galleryGridRef,
  onCategorySelect = Function.prototype,
  onTemplatePreviewClick = Function.prototype,
  onTemplateEditClick = Function.prototype,
  onBlankTemplateClick = Function.prototype,
  onFilterTemplateClick = Function.prototype,
  gridColumns = PREVIEW_COLUMNS_COUNT.twoColumns,
  handleColumnsToggle,
  onFloatingButtonClick = Function.prototype,
  onLoadMoreClick = Function.prototype,
  isNextTemplatesBatchLoading,
  isFirstTemplatesBatchLoading,
  isFloatingButtonVisible,
  isAISiteWizardEnabled = false,
}) {
  // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  const { number: currentStep } = useMemo(helpers.getCurrentStep, []);

  return (
    <Layout Header={HeaderGalleryPageContainer}>
      <SiteSetupLayout
        className="ws-site-setup-layout-gallery"
        captions={captions}
        currentStep={currentStep}
      >
        <CategoriesBar
          ref={categoriesBarRef}
          control={control}
          onCategorySelect={onCategorySelect}
          onBlankTemplateClick={onBlankTemplateClick}
          gridColumns={gridColumns}
          onFilterTemplateClick={onFilterTemplateClick}
          handleColumnsToggle={handleColumnsToggle}
        />
        {recommendedTemplates.length > 0 ? (
          <>
            <GalleryGridSection
              isLoading={isFirstTemplatesBatchLoading}
              templatesLabel={captions.recommendedTemplatesLabel}
              templates={recommendedTemplates}
              gridColumns={gridColumns}
              galleryGridRef={galleryGridRef}
              isAISiteWizardEnabled={isAISiteWizardEnabled}
              onTemplatePreviewClick={onTemplatePreviewClick}
              onTemplateEditClick={onTemplateEditClick}
            />
            <GalleryGridSection
              isLoading={isFirstTemplatesBatchLoading}
              templatesLabel={captions.allTemplatesLabel}
              templates={templates}
              gridColumns={gridColumns}
              isAISiteWizardEnabled={isAISiteWizardEnabled}
              onTemplatePreviewClick={onTemplatePreviewClick}
              onTemplateEditClick={onTemplateEditClick}
            />
          </>
        ) : (
          <div className="ws-site-setup-layout-gallery__list">
            <GalleryGrid
              isLoading={isFirstTemplatesBatchLoading}
              isAISiteWizardEnabled={isAISiteWizardEnabled}
              ref={galleryGridRef}
              templates={templates}
              gridColumns={gridColumns}
              onTemplatePreviewClick={onTemplatePreviewClick}
              onTemplateEditClick={onTemplateEditClick}
            />
          </div>
        )}
        <GalleryPagination
          isLoading={isNextTemplatesBatchLoading}
          canLoadMore={canLoadMore}
          captions={captions}
          onLoadMoreClick={onLoadMoreClick}
        />

        {isFloatingButtonVisible && (
          <div className="ws-site-setup-layout-gallery__floating-button">
            <FloatingAction onClick={onFloatingButtonClick} iconGlyph="arrow-up" />
          </div>
        )}
      </SiteSetupLayout>
    </Layout>
  );
}

GalleryLayoutStep.propTypes = {
  captions: PropTypes.shape({
    title: PropTypes.string,
    subtitle: PropTypes.string,
    recommendedTemplatesLabel: PropTypes.string,
    allTemplatesLabel: PropTypes.string,
    loadMoreButton: PropTypes.string,
  }).isRequired,
  control: PropTypes.shape({}).isRequired,
  templates: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      slug: PropTypes.string,
      previewUrl: PropTypes.string,
    })
  ).isRequired,
  recommendedTemplates: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      slug: PropTypes.string,
      previewUrl: PropTypes.string,
    })
  ),
  isNextTemplatesBatchLoading: PropTypes.bool.isRequired,
  isFirstTemplatesBatchLoading: PropTypes.bool.isRequired,
  isFloatingButtonVisible: PropTypes.bool.isRequired,
  canLoadMore: PropTypes.bool.isRequired,
  categoriesBarRef: PropTypes.shape({
    current: PropTypes.any,
  }).isRequired,
  galleryGridRef: PropTypes.shape({
    current: PropTypes.any,
  }).isRequired,
  gridColumns: PropTypes.oneOf(Object.values(PREVIEW_COLUMNS_COUNT)),
  onCategorySelect: PropTypes.func,
  onLoadMoreClick: PropTypes.func,
  onFloatingButtonClick: PropTypes.func,
  onTemplatePreviewClick: PropTypes.func,
  onTemplateEditClick: PropTypes.func,
  onBlankTemplateClick: PropTypes.func,
  onFilterTemplateClick: PropTypes.func,
  handleColumnsToggle: PropTypes.func.isRequired,
  isAISiteWizardEnabled: PropTypes.bool,
};

export default React.memo(GalleryLayoutStep);
